import { useRef } from "react";
import { Camera, Trash2, Type } from "lucide-react";
import { toast } from "sonner";
import { HomeOSAvatar } from "./HomeOSAvatar";

const FAMILY_STONE = "#7A6E63";

/* Resize an image file to a 256×256 square (cover) and return a base64 PNG.
   This keeps localStorage small while still looking sharp on retina. */
async function fileToCompressedDataUrl(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = max; canvas.height = max;
          const ctx = canvas.getContext("2d");
          // cover crop
          const ratio = Math.max(max / img.width, max / img.height);
          const w = img.width * ratio, h = img.height * ratio;
          const dx = (max - w) / 2, dy = (max - h) / 2;
          ctx.drawImage(img, dx, dy, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function AvatarUploader({ member, onChange }) {
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file."); return; }
    try {
      const dataUrl = await fileToCompressedDataUrl(file, 256);
      onChange({ avatarType: "photo", photoUrl: dataUrl });
      toast.success("Photo updated.");
    } catch {
      toast.error("Couldn't load that image. Try another file.");
    }
    e.target.value = "";
  };

  const useInitials = () => {
    onChange({ avatarType: "initial", photoUrl: null, color: FAMILY_STONE });
  };
  const removePhoto = () => {
    onChange({ avatarType: "initial", photoUrl: null, color: FAMILY_STONE });
    toast("Photo removed.");
  };

  return (
    <div className="rounded-[14px] p-4" style={{ border: "1px solid var(--ara-line)" }} data-testid="avatar-uploader">
      <span className="block text-[11px] tracking-[0.14em] uppercase" style={{ fontFamily: "Inter, system-ui, sans-serif", color: "var(--ara-ink-soft)", fontWeight: 500 }}>Avatar</span>
      <div className="mt-3 flex items-center gap-4">
        <HomeOSAvatar member={member} size="xl" />

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 text-[12px] px-3.5 py-2 min-h-[36px] rounded-full transition-opacity hover:opacity-70"
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 450, color: "var(--ara-ink)", background: "transparent", border: "1px solid var(--ara-line)", cursor: "pointer" }}
              data-testid="avatar-upload-btn"
            >
              <Camera className="w-3.5 h-3.5" strokeWidth={1.5} />
              {member?.avatarType === "photo" ? "Change photo" : "Upload photo"}
            </button>
            {member?.avatarType === "photo" && (
              <button
                type="button"
                onClick={removePhoto}
                className="inline-flex items-center gap-1.5 text-[12px] px-3 py-2 min-h-[36px] rounded-full transition-opacity hover:opacity-70"
                style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 400, color: "var(--ara-ink-soft)", background: "none", border: "none", cursor: "pointer" }}
                data-testid="avatar-remove-btn"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />Remove
              </button>
            )}
            <button
              type="button"
              onClick={useInitials}
              className="inline-flex items-center gap-1.5 text-[12px] px-3 py-2 min-h-[36px] rounded-full transition-opacity hover:opacity-70"
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 400, color: "var(--ara-ink-soft)", background: "none", border: "none", cursor: "pointer" }}
              data-testid="avatar-initials-btn"
            >
              <Type className="w-3.5 h-3.5" strokeWidth={1.5} />Use initials
            </button>
          </div>

          <p className="text-[11px] italic" style={{ fontFamily: "Inter, system-ui, sans-serif", color: "var(--ara-ink-soft)" }}>
            Photos stay on this device for now.
          </p>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="avatar-file-input" />
      </div>
    </div>
  );
}
